import { Node, mergeAttributes } from '@tiptap/core';
import { ReactNodeViewRenderer } from '@tiptap/react';
import { PLACEHOLDERS, isPlaceholderKey, type PlaceholderKey } from '@reditelji/shared';
import { CtaPill, type CtaOptions } from './CtaPill';

/**
 * The two node types the mail editor adds on top of ordinary rich text.
 *
 * Both round-trip through the same markup the shared renderer reads:
 *
 *   inline   <span data-ph="ime">{{ime}}</span>
 *   block    <div data-ph="razpored">{{razpored}}</div>
 *   button   <a data-cta href="{{povezava}}">Odpri brifing</a>
 *
 * Storage keeps the braces so a body stays legible — and still renders — if it
 * is ever read or edited outside this screen. The chips a coordinator sees are
 * a node view over that markup, not a different format.
 */

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    placeholder: {
      insertPlaceholder: (key: PlaceholderKey) => ReturnType;
    };
    cta: {
      insertCta: (attrs: { label: string; href: string }) => ReturnType;
    };
  }
}

function chip(key: string, block: boolean): HTMLElement {
  const def = isPlaceholderKey(key) ? PLACEHOLDERS[key] : null;
  const dom = document.createElement(block ? 'div' : 'span');
  dom.className = block
    ? 'ml-chip ml-chip--block'
    : `ml-chip ml-chip--${def?.group ?? 'unknown'}`;
  dom.dataset.ph = key;
  dom.contentEditable = 'false';
  if (def) dom.title = def.hint;
  if (block) {
    const name = document.createElement('span');
    name.className = 'ml-chip-name';
    name.textContent = key;
    const hint = document.createElement('span');
    hint.className = 'ml-chip-hint';
    hint.textContent = def?.hint ?? 'neznana oznaka';
    dom.append(name, hint);
  } else {
    dom.textContent = key;
    if (!def) dom.classList.add('ml-chip--unknown');
  }
  return dom;
}

/** ⟦ime⟧, ⟦dogodek⟧ … — one token inside a sentence, deleted as a unit. */
export const InlinePlaceholder = Node.create({
  name: 'placeholder',
  group: 'inline',
  inline: true,
  atom: true,
  selectable: true,

  addAttributes() {
    return { key: { default: 'ime' } };
  },

  parseHTML() {
    return [
      {
        tag: 'span[data-ph]',
        getAttrs: (el) => {
          const key = el.dataset.ph ?? '';
          const def = isPlaceholderKey(key) ? PLACEHOLDERS[key] : null;
          // Block placeholders are the other node; let it claim them.
          return def?.block ? false : { key };
        },
      },
    ];
  },

  renderHTML({ node }) {
    const key = String(node.attrs.key);
    return ['span', mergeAttributes({ 'data-ph': key }), `{{${key}}}`];
  },

  addNodeView() {
    return ({ node }) => ({ dom: chip(String(node.attrs.key), false) });
  },

  addCommands() {
    return {
      insertPlaceholder:
        (key) =>
        ({ commands }) =>
          commands.insertContent({ type: this.name, attrs: { key } }),
    };
  },
});

/**
 * ⟦razpored⟧, ⟦spremembe⟧ — content the app composes, so it stands on its own
 * line. Being a block node is what lets the renderer drop it whole when there
 * is nothing to put there: it can never be half of somebody's sentence.
 */
export const BlockPlaceholder = Node.create({
  name: 'placeholderBlock',
  group: 'block',
  atom: true,
  selectable: true,
  draggable: true,

  addAttributes() {
    return { key: { default: 'razpored' } };
  },

  parseHTML() {
    return [
      {
        tag: 'div[data-ph]',
        getAttrs: (el) => ({ key: el.dataset.ph ?? '' }),
      },
    ];
  },

  renderHTML({ node }) {
    const key = String(node.attrs.key);
    return ['div', mergeAttributes({ 'data-ph': key }), `{{${key}}}`];
  },

  addNodeView() {
    return ({ node }) => ({ dom: chip(String(node.attrs.key), true) });
  },
});

/** The call-to-action button, rendered in mail as a table cell (see shared/mail.ts). */
export const CtaButton = Node.create<CtaOptions>({
  name: 'cta',
  group: 'block',
  atom: true,
  selectable: true,

  addOptions() {
    return { allowsPovezava: true };
  },

  addAttributes() {
    return {
      label: { default: 'Odpri brifing' },
      href: { default: '{{povezava}}' },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'a[data-cta]',
        /*
         * Above the default 50, so this rule is tried before Link's `a[href]`:
         * both claim <a>, and the other way round the button comes back from
         * storage as an ordinary link with the label as its text.
         *
         * The rule's own priority is the whole of it. The *extension's*
         * priority has to stay at the default, because extension order is
         * schema node order, and the first block node in the schema is what
         * `doc`'s `block+` resolves to — the type the editor builds wherever it
         * needs some block. Put `cta` above Paragraph (1000) and the trailing
         * node, `createParagraphNear` and `doc.createAndFill()` all reach for a
         * button: the letter grows one at its end on every keystroke, and
         * deleting one only makes another. `extensions.test.ts` holds both ends
         * of this down.
         */
        priority: 100,
        getAttrs: (el) => ({
          label: el.textContent ?? '',
          href: el.getAttribute('href') ?? '',
        }),
      },
    ];
  },

  renderHTML({ node }) {
    return [
      'a',
      mergeAttributes({ 'data-cta': '', href: String(node.attrs.href) }),
      String(node.attrs.label),
    ];
  },

  /*
   * A React view rather than plain DOM, because the pill is the only chip a
   * coordinator can open: it carries the button's own two fields (see CtaPill).
   *
   * The class goes on Tiptap's wrapper, which is the element ProseMirror marks
   * as the selected node — so it is the only place from which the selection
   * outline can be made to hug the button rather than the whole line.
   */
  addNodeView() {
    return ReactNodeViewRenderer(CtaPill, { className: 'ml-cta-node' });
  },

  addCommands() {
    return {
      insertCta:
        (attrs) =>
        ({ commands }) =>
          commands.insertContent({ type: this.name, attrs }),
    };
  },
});
